/**
 * TableFrame — the quiet tea garden composition the tiles sit in.
 *
 * Two genuine times of day (not an inversion — see POLISH-GATE item 7):
 *   - light = soft garden DAYLIGHT: a pale open sky behind, a honey-wood tea
 *     table lit from the upper-left, a faint wash of leaf-green at the edges.
 *   - dark  = paper-LANTERN evening: the same table under a single warm pool of
 *     lantern light on dark walnut, the garden receded into indigo night.
 *
 * The atmosphere is the PAGE; the table is a clean wood surface raised on warm
 * multi-layer shadows; the tiles live on the table. Texture (wood grain) comes
 * from an SVG <filter> (feTurbulence) — never hand-drawn imagery.
 */
import type { Theme } from '@/preferences';

export interface TableFrameProps {
  theme: Theme;
  children: React.ReactNode;
}

export function TableFrame({ theme, children }: TableFrameProps): React.JSX.Element {
  return (
    <div className="mj-frame" data-theme={theme}>
      {/* The garden / sky beyond the table. */}
      <div className="mj-frame__garden" aria-hidden="true">
        <div className="mj-frame__sky" />
        <div className="mj-frame__horizon" />
        {/* The light source — daylight from the upper-left, or the lantern glow. */}
        <div className="mj-frame__light" />
      </div>

      {/* The wood tea table, raised on warm shadows. */}
      <div className="mj-frame__table" aria-hidden="true">
        <WoodGrain theme={theme} />
        <div className="mj-frame__table-sheen" />
      </div>

      {/* The content (header, board, status) sits over the table. */}
      <div className="mj-frame__content">{children}</div>
    </div>
  );
}

/** A subtle wood-grain texture via feTurbulence — structural filter, not art. */
function WoodGrain({ theme }: { theme: Theme }): React.JSX.Element {
  const id = `mj-grain-${theme}`;
  return (
    <svg className="mj-frame__grain" aria-hidden="true" width="100%" height="100%">
      <defs>
        <filter id={id} x="0" y="0" width="100%" height="100%">
          {/* Stretched horizontally → long wood-grain streaks rather than noise. */}
          <feTurbulence type="fractalNoise" baseFrequency="0.012 0.16" numOctaves="3" seed="7" result="n" />
          <feColorMatrix
            in="n"
            type="matrix"
            values={
              theme === 'dark'
                ? '0 0 0 0 0.08  0 0 0 0 0.05  0 0 0 0 0.02  0 0 0 0.5 0'
                : '0 0 0 0 0.45  0 0 0 0 0.31  0 0 0 0 0.16  0 0 0 0.32 0'
            }
          />
        </filter>
      </defs>
      <rect width="100%" height="100%" filter={`url(#${id})`} />
    </svg>
  );
}
